import {IconCopy} from "@tabler/icons-react";
import {message} from "@ui";
import PropTypes from "prop-types";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMobile from "@/hooks/useIsMobile";
import imgBancamiga from "../../../assets/images/modal-icons/bancamiga-icon.svg";
import imgBankPlaza from "../../../assets/images/modal-icons/banco-plaza-icon.png";
import imgBankVzla from "../../../assets/images/modal-icons/bdv-icon.png";
import imgUbiiPago from "../../../assets/images/modal-icons/ubi-pago-logo.svg";
import {useAppConfig} from "../../../store/useAppConfig.store";
import {useUserSupport} from "../../../store/useUserSupport.store";
import {PrimaryButton} from "../../Button";
import depositStyles from "../DepositMethods.module.css";
import styles from "./bankOptions.module.css";
import UbiiPagoOrphanClaimModal from "@/components/Modals/UbiiPagoOrphanClaimModal";

const BANCAMIGA_INFO = {
  bankName: "Bancamiga",
  RIF: "J-504732001",
  phone: "04127230400",
};

const BDV_BANK_NAME = "Banco de Venezuela";

const BankOptions = ({onComplete}) => {
  const [bankSelected, setBankSelected] = useState("");
  const [showUbiiPagoModal, setShowUbiiPagoModal] = useState(false);
  const {t} = useTranslation();
  const isMobile = useIsMobile();

  const configBankName = useAppConfig(state => state.bankName);
  const configRIF = useAppConfig(state => state.RIF);
  const configPhone = useAppConfig(state => state.PagoMovilNumber);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const banks = useMemo(
    () => [
      {
        id: "plaza",
        alt: "Banco Plaza",
        img: imgBankPlaza,
        info: {
          bankName: configBankName,
          RIF: configRIF,
          phone: configPhone,
        },
      },
      {
        id: "bdv",
        alt: "Banco de Venezuela",
        img: imgBankVzla,
        info: {
          bankName: BDV_BANK_NAME,
          RIF: configRIF,
          phone: configPhone,
        },
      },
      {
        id: "bancamiga",
        alt: "Bancamiga",
        img: imgBancamiga,
        info: BANCAMIGA_INFO,
      },
    ],
    [configBankName, configRIF, configPhone]
  );

  const selectedBank = banks.find(bank => bank.id === bankSelected);

  const handleUbiiPagoClick = () => {
    setBankSelected("");
    setShowUbiiPagoModal(true);
  };

  const copyToClipboard = async text => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success(t("depositInstructionsCopied"));
    } catch {
      message.error(t("depositInstructionsError"));
    }
  };

  const paymentRows = selectedBank
    ? [
        {
          key: "bank",
          label: t("depositInstructionsBank"),
          value: selectedBank.info.bankName,
        },
        {
          key: "rif",
          label: "RIF",
          value: selectedBank.info.RIF,
        },
        {
          key: "phone",
          label: t("depositInstructionsMobilePhone"),
          value: selectedBank.info.phone,
        },
      ]
    : [];

  return (
    <div className={styles.depositLayout}>
      <p className={styles.bankSelectLabel}>{t("depositInstructionsBankOptionsTitle")}</p>

      <div className={depositStyles.bankOptionsGroup}>
        {banks.map(bank => (
          <button
            key={bank.id}
            type="button"
            onClick={() => setBankSelected(bank.id)}
            className={`${depositStyles.bankOptionCard} ${
              bankSelected === bank.id ? depositStyles.bankOptionCardSelected : ""
            }`}
            aria-pressed={bankSelected === bank.id}
          >
            <img
              src={bank.img}
              alt={bank.alt}
            />
          </button>
        ))}

        <button
          type="button"
          onClick={handleUbiiPagoClick}
          className={depositStyles.bankOptionCard}
          aria-pressed={false}
        >
          <img
            src={imgUbiiPago}
            alt="Ubii Pago"
          />
        </button>
      </div>

      {selectedBank && (
        <div className={styles.paymentInfoCard}>
          {paymentRows.map(row => (
            <button
              key={row.key}
              type="button"
              className={styles.paymentInfoRow}
              onClick={() => copyToClipboard(row.value)}
            >
              <div className={styles.paymentInfoLabel}>
                <span className={styles.paymentInfoKey}>{row.label}</span>
                <span className={styles.paymentInfoValue}>{row.value}</span>
              </div>
              <IconCopy
                size={16}
                className={styles.copyIcon}
              />
            </button>
          ))}
        </div>
      )}

      <p className={styles.creditSoonHint}>{t("depositInstructionsCreditSoon")}</p>

      <div className={styles.supportHint}>
        <p>
          {t("depositInstructionsBankVzlaSupportMessage")}{" "}
          <button
            type="button"
            className={styles.supportHintLink}
            onClick={handleSupportModal}
          >
            {t("depositInstructionsBankVzlaSupportLink")}
          </button>
        </p>
      </div>

      <div className={styles.depositFooterCentered}>
        <PrimaryButton
          block
          onClick={onComplete}
        >
          {t("depositInstructionsPaymentDone")}
        </PrimaryButton>
      </div>

      {!(isMobile && bankSelected) && (
        <p className={styles.depositInstructions}>{t("depositInstructionsMessagePlaza")}</p>
      )}

      <UbiiPagoOrphanClaimModal
        open={showUbiiPagoModal}
        onClose={() => setShowUbiiPagoModal(false)}
      />
    </div>
  );
};

BankOptions.propTypes = {
  onComplete: PropTypes.func.isRequired,
};

export default BankOptions;
